import { z } from "zod";

function checkFileType(file: File) {
  if (file?.name) {
    const fileType = file.name.split(".").pop();

    if (fileType === "jpeg" || fileType === "jpg" || fileType === "png") return true;
  }

  return false;
}

export const getHeaderSchema = (t: any) =>
  z.object({
    name: z.string().min(3, t("forms:validation.name_min_length")).optional(),
    headline: z.string().min(3, t("forms:validation.headline_min_length")).optional().or(z.literal("")),
    current_location_name: z.string().optional(),
    current_location_coords: z
      .object({
        lat: z.number(),
        lng: z.number(),
      })
      .optional(),
    avatar: z
      .any()
      .refine((file) => {
        if (file === undefined || file === "") {
          return true;
        }

        return file.size < 2048000;
      }, t("forms:validation.avatar_max_size"))
      .refine((file) => {
        if (file === undefined || file === "") {
          return true;
        }

        return checkFileType(file);
      }, t("forms:validation.image_format"))
      .optional(),
    background: z
      .any()
      .refine((file) => {
        if (file === undefined || file === "") {
          return true;
        }

        return file.size < 5120000;
      }, t("forms:validation.background_max_size"))
      .refine((file) => {
        if (file === undefined || file === "") {
          return true;
        }

        return checkFileType(file);
      }, t("forms:validation.image_format"))
      .optional(),
  });

export type HeaderFormData = z.infer<ReturnType<typeof getHeaderSchema>>;

export const getAboutSchema = (t: any) =>
  z.object({
    description: z
      .string()
      .min(10, t("forms:validation.description_min_length"))
      .max(5000, t("forms:validation.description_max_length"))
      .optional(),
    subject_ids: z.array(z.string()).min(1, t("forms:validation.subject_required")).optional(),
  });

export type AboutFormData = z.infer<ReturnType<typeof getAboutSchema>>;

const getEducationSchema = (t: any) =>
  z.object({
    id: z.string().optional(),
    institution_name: z
      .string()
      .min(1, t("forms:validation.institution_required"))
      .max(255, t("forms:validation.institution_max_length")),
    degree: z.string().max(255, t("forms:validation.degree_max_length")).optional(),
    field_of_study: z.string().max(255, t("forms:validation.field_max_length")).optional(),
    start_date: z.string().optional(),
    end_date: z.string().optional(),
    description: z.string().max(1000, t("forms:validation.edu_description_max_length")).optional(),
  });

export const getEducationSectionSchema = (t: any) =>
  z.object({
    education: z.array(getEducationSchema(t)).min(1, t("forms:validation.education_required")),
  });

export type EducationFormData = z.infer<ReturnType<typeof getEducationSectionSchema>>;

const getCertificateSchema = (t: any) =>
  z.object({
    id: z.string().optional(),
    name: z
      .string()
      .min(1, t("forms:validation.certificate_name_required"))
      .max(255, t("forms:validation.certificate_name_max_length")),
    issuing_organization: z
      .string()
      .min(1, t("forms:validation.issuing_org_required"))
      .max(255, t("forms:validation.issuing_org_max_length")),
    issue_date: z.string().or(z.date()).optional(),
    expiration_date: z.string().or(z.date()).optional(),
    credential_id: z.string().max(255, t("forms:validation.credential_id_max_length")).optional(),
    credential_url: z.string().url(t("forms:validation.valid_url")).or(z.literal("")).optional(),
    description: z.string().max(1000, t("forms:validation.cert_description_max_length")).optional(),
  });

export const getCertificatesSectionSchema = (t: any) =>
  z.object({
    certificates: z
      .array(getCertificateSchema(t))
      .min(1, t("forms:validation.certificate_required")),
  });

export type CertificatesFormData = z.infer<ReturnType<typeof getCertificatesSectionSchema>>;

const getSubjectSchema = (t: any) =>
  z.object({
    id: z.string(),
    curriculum_levels: z.array(z.string()).min(1, t("forms:validation.curriculum_level_required")),
  });

export const getSubjectsSectionSchema = (t: any) =>
  z.object({
    subjects: z.array(getSubjectSchema(t)).min(1, t("forms:validation.subject_required")),
  });

export type SubjectsFormData = z.infer<ReturnType<typeof getSubjectsSectionSchema>>;

const getLanguageSchema = z.object({
  id: z.string(),
  name: z.string(),
  proficiency_slug: z.string(),
  proficiency_label: z.string(),
});

export const getLanguagesSectionSchema = (t: any) =>
  z.object({
    languages: z.array(getLanguageSchema).min(1, t("forms:validation.language_required")),
  });

export type LanguagesFormData = z.infer<ReturnType<typeof getLanguagesSectionSchema>>;
